import { Button, InlineNotification, Tile } from "@carbon/react";
import { useTranslation } from "react-i18next";
import { SampleEnum } from "../../types";
import "./sampleItem.scss";

type SampleItemProps = {
  stateSample: SampleEnum;
  handleApproveSample: () => void;
  handleRejectSample: () => void;
  userCompanyType: string | null;
};

/**
 * The sample-verification block inside the supplier offer detail — rebuilt on Carbon. A requested
 * sample renders its message on a `Tile` with Carbon approve/reject `Button`s; a verified or
 * rejected sample renders a low-contrast `InlineNotification`. The pre-Carbon raw `<button>`s and
 * hand-coloured status boxes are gone; the approve/reject handlers are unchanged.
 */
export default function SampleItem({
  stateSample,
  handleApproveSample,
  handleRejectSample,
  userCompanyType,
}: SampleItemProps) {
  const { t } = useTranslation();
  const isRequested = stateSample === SampleEnum.REQUESTED;
  const isRejected = stateSample === SampleEnum.REJECTED;
  const isVerified = stateSample === SampleEnum.VERIFIED;

  if (isRequested) {
    return (
      <Tile className="sample-item">
        <p className="sample-item__message">{t("offer.sampleMessage")}</p>
        {!userCompanyType && (
          <div className="sample-item__actions">
            <Button kind="secondary" size="sm" onClick={handleApproveSample}>
              {t("offer.approveSample")}
            </Button>
            <Button
              kind="danger--tertiary"
              size="sm"
              onClick={handleRejectSample}
            >
              {t("offer.rejectSample")}
            </Button>
          </div>
        )}
      </Tile>
    );
  }

  if (isVerified) {
    return (
      <InlineNotification
        className="sample-item__status"
        kind="success"
        lowContrast
        hideCloseButton
        title={t("offer.verifiedSample")}
      />
    );
  }

  if (isRejected) {
    return (
      <InlineNotification
        className="sample-item__status"
        kind="error"
        lowContrast
        hideCloseButton
        title={t("offer.rejectedSample")}
      />
    );
  }

  return null;
}
